import { forwardRef } from "react"
import styled, { css } from "styled-components"
import { Box } from "@fastly/consistently"
import { H5 } from "@fastly/consistently-vanilla"
import { MikanSpan } from "@components/Mikan"

export const EstimatorGrid = styled.div(
  ({ theme }) => css`
    display: grid;
    grid-template-columns: 1.5fr 1fr;
    gap: 1.5rem 0.5rem;

    & > *:nth-child(3) {
      grid-column: 1 / span 2;
    }

    @media (min-width: ${theme.breakpoints.sm}) {
      grid-template-columns: 1.75fr 1fr 1fr;

      & > *:nth-child(3) {
        grid-column: auto;
      }
    }
  `
)

const fieldStyles = css`
  border: solid var(--grey-60) 1px;
  border-top-width: ${({ theme }) => theme.space["2xs"]};
  background-color: var(--grey-20);
  font-family: var(--font);
  max-width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space["2xs"]};
  padding: ${({ theme }) => `${theme.space["2xs"]} ${theme.space.xs}`};
`

const labelStyles = css`
  display: block;
  color: var(--grey-90);
  font: var(--text-sm);
  font-weight: 600;
  line-height: 0.9rem;
  width: 100%;
`

const InputWrapper = styled(Box)`
  ${fieldStyles}

  &:focus-within {
    border: solid var(--blue-dark) 1px;
    border-top-width: ${({ theme }) => theme.space["2xs"]};
  }

  &[data-error="true"] {
    border-color: var(--color-red-70);
  }
`

const Error = styled.span`
  color: var(--color-red-70);
  font-size: var(--font-size-xs);
  position: absolute;
  padding-top: var(--size-space-2xs);
`

const OutputWrapper = styled.div`
  ${fieldStyles}
  border-color: transparent;
`

const InputLabel = styled.label`
  ${labelStyles}
  margin: 0;
`

const OutputLabel = styled.span`
  ${labelStyles}
  color: var(--grey-60);
`

const StyledInput = styled.input`
  all: unset;
  width: 100%;
`

const OutputCurrencyIndicator = styled.span`
  font-weight: 600;
  display: inline-block;
  margin-right: ${({ theme }) => theme.space.xs};
`

// eslint-disable-next-line react/display-name
export const Input = forwardRef(({ label, id, onChange, value, error, ...props }, ref) => {
  return (
    <Box>
      <InputWrapper {...props} ref={ref}>
        <InputLabel htmlFor={id}>
          <MikanSpan text={label} />
        </InputLabel>
        <StyledInput onChange={onChange} id={id} name={id} value={value} type="text" pattern="[0-9]+" />
      </InputWrapper>
      {error ? <Error>{error}</Error> : null}
    </Box>
  )
})

export const Output = ({ label, children, id }) => (
  <OutputWrapper>
    <OutputLabel id={id}>
      <MikanSpan text={label} />
    </OutputLabel>
    <div aria-labelledby={id}>{children}</div>
  </OutputWrapper>
)

export const CurrencyOutput = ({ label, children }) => (
  <Output label={label}>
    <OutputCurrencyIndicator>$</OutputCurrencyIndicator>
    {children}
  </Output>
)

const StyledTotal = styled.div(
  ({ theme }) => css`
    padding: ${theme.space.md};
    border-bottom: 1px solid var(--grey-darkest);

    @media (min-width: ${theme.breakpoints.sm}) {
      padding-left: ${theme.space["4xl"]};
    }
  `
)

export const TotalHeader = ({ children }) => (
  <StyledTotal>
    <H5 m={0}>{children}</H5>
  </StyledTotal>
)
